import { useUpdateExperience } from '@/lib/queries';
import type { AuthoringDoc } from '@personalizehq/editor';
import { useEffect, useRef, useState } from 'react';

export type SaveState = 'saved' | 'dirty' | 'saving' | 'error';

/** Drafts autosave; PUBLISH stays explicit and versioned (spec §4) — no amount of typing here can
 *  change what a device is currently rendering. The debounce is the whole design: every keystroke
 *  is a new doc object (immer), and a PATCH per keystroke would bump `version` per keystroke too. */
export function useAutosave(experienceId: string, appId: string | null, doc: AuthoringDoc): SaveState {
  const update = useUpdateExperience(experienceId, appId);
  const [state, setState] = useState<SaveState>('saved');
  const saved = useRef(doc);

  useEffect(() => {
    // The doc as loaded is not an edit. Without this every open would immediately PATCH and bump
    // the version of an experience nobody touched.
    if (doc === saved.current) return;
    setState('dirty');

    const timer = setTimeout(() => {
      setState('saving');
      update.mutate(
        { document: doc },
        {
          onSuccess: () => {
            saved.current = doc;
            setState('saved');
          },
          onError: () => setState('error'),
        }
      );
    }, 1000);

    return () => clearTimeout(timer);
    // `update` is a fresh object each render; depending on it would re-arm the timer forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  return state;
}
